import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useFizbo, type FizboListing } from '@/hooks/useFizbo';
import { smartSearchSummary } from '@/mocks/fizboData';

export default function AkilliArama() {
  const { isDark } = useTheme();
  const { listings } = useFizbo();
  const [inputValue, setInputValue] = useState('');
  const [searchedListing, setSearchedListing] = useState<FizboListing | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSearch = () => {
    if (!inputValue.trim()) return;
    setLoading(true);
    setNotFound(false);
    setSearchedListing(null);

    setTimeout(() => {
      const found = listings.find(
        (l) => l.listingNo.toLowerCase() === inputValue.trim().toLowerCase()
      );
      if (found) {
        setSearchedListing(found);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    }, 800);
  };

  const summary = smartSearchSummary;
  const maxWeekly = Math.max(...summary.weeklyInterest);

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div>
        <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          İlan No ile Hızlı Analiz
        </p>
        <div className="flex gap-2">
          <div className={`flex items-center gap-2 flex-1 px-3 py-2.5 rounded-xl border ${
            isDark ? 'bg-dark-bg border-dark-border' : 'bg-light-bg border-light-border'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-search-line text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Örn: FZB-2026-001"
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
              }`}
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
          >
            {loading ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-flashlight-line" />
            )}
            Analiz Et
          </button>
        </div>

        {/* Quick Suggestions */}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {listings.slice(0, 4).map((l) => (
            <button
              key={l.id}
              onClick={() => { setInputValue(l.listingNo); }}
              className={`text-xs px-2 py-1 rounded-lg cursor-pointer transition-colors ${
                isDark ? 'bg-dark-border text-dark-muted hover:text-dark-text' : 'bg-light-border text-light-muted hover:text-light-text'
              }`}
            >
              {l.listingNo}
            </button>
          ))}
        </div>
      </div>

      {/* Not Found */}
      {notFound && (
        <div className={`rounded-xl p-4 text-center ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
          <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <i className="ri-search-line text-red-500" />
          </div>
          <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>İlan bulunamadı</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            &quot;{inputValue}&quot; ile eşleşen ilan yok.
          </p>
        </div>
      )}

      {/* Result Panel */}
      {searchedListing && (
        <div className="space-y-3 animate-fade-in">
          {/* Header */}
          <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className={`text-sm font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  {searchedListing.ownerName}
                </p>
                <p className={`text-xs font-mono mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  {searchedListing.listingNo}
                </p>
              </div>
              {searchedListing.sellabilityScore && (
                <div className={`flex flex-col items-center px-3 py-1.5 rounded-xl ${
                  searchedListing.sellabilityScore >= 80
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : searchedListing.sellabilityScore >= 60
                    ? 'bg-yellow-100 dark:bg-yellow-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <span className={`text-xl font-bold ${
                    searchedListing.sellabilityScore >= 80 ? 'text-green-600 dark:text-green-400' :
                    searchedListing.sellabilityScore >= 60 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {searchedListing.sellabilityScore}
                  </span>
                  <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>skor</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Lokasyon', value: searchedListing.location, icon: 'ri-map-pin-line' },
                { label: 'Fiyat', value: searchedListing.price ? `${(searchedListing.price / 1000000).toFixed(2)}M ₺` : '-', icon: 'ri-price-tag-3-line' },
                { label: 'İlan Yaşı', value: `${searchedListing.daysOld} gün`, icon: 'ri-time-line' },
                { label: 'Durum', value: searchedListing.status, icon: 'ri-flag-line' },
              ].map((item) => (
                <div key={item.label} className={`flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-dark-card' : 'bg-white'}`}>
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-xs text-primary-500`} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{item.label}</p>
                    <p className={`text-xs font-semibold truncate ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Market Summary */}
          <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
            <p className={`text-xs font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Piyasa Özeti
            </p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { label: 'Fiyat Trendi', value: summary.priceTrend.label, color: 'text-red-500', icon: 'ri-arrow-down-line' },
                { label: 'Rakip İlan', value: `${summary.competitorCount} aktif`, color: isDark ? 'text-dark-text' : 'text-light-text', icon: 'ri-building-2-line' },
                { label: 'Talep', value: summary.demandLevel, color: 'text-green-500', icon: 'ri-fire-line' },
              ].map((item) => (
                <div key={item.label} className={`rounded-lg p-2.5 text-center ${isDark ? 'bg-dark-card' : 'bg-white'}`}>
                  <div className="w-5 h-5 flex items-center justify-center mx-auto mb-1">
                    <i className={`${item.icon} text-sm ${item.color}`} />
                  </div>
                  <p className={`text-xs font-bold ${item.color}`}>{item.value}</p>
                  <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{item.label}</p>
                </div>
              ))}
            </div>

            {/* Weekly Interest Mini Chart */}
            <p className={`text-xs mb-2 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Son 7 Gün İlgi</p>
            <div className="flex items-end gap-1 h-10">
              {summary.weeklyInterest.map((val, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t bg-primary-500/70 transition-all duration-500"
                  style={{ height: `${(val / maxWeekly) * 100}%` }}
                />
              ))}
            </div>
          </div>

          {/* Recommendation */}
          <div className={`rounded-xl p-4 border-l-4 border-primary-500 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-lightbulb-flash-line text-sm text-primary-500" />
              </div>
              <div>
                <p className={`text-xs font-semibold mb-0.5 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  Akıllı Öneri
                </p>
                <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  {summary.recommendedAction}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Default State - No Search Yet */}
      {!searchedListing && !notFound && !loading && (
        <div className={`rounded-xl p-5 text-center ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <i className="ri-flashlight-line text-primary-500 text-xl" />
          </div>
          <p className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            Akıllı Arama Ön Bilgi
          </p>
          <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            İlan no girerek fiyat trendi, rakip durumu ve müşteri ilgisi hakkında güçlü özet bilgiler alın.
          </p>
        </div>
      )}
    </div>
  );
}
